import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EMAIL_EVENTS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Eye,
  Loader2,
  MailWarning,
  MousePointerClick,
  RefreshCw,
  Send,
  Zap,
} from "lucide-react";

const SHARE_TOKENS_KEY = "imali:share-tokens";
const loadShareTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(SHARE_TOKENS_KEY) || "{}");
  } catch {
    return {};
  }
};

const STAT_CARDS = [
  {
    key: "email.sent",
    label: "Sent",
    kind: "sent",
    Icon: Send,
    tone: "text-foreground",
  },
  {
    key: "email.delivered",
    label: "Delivered",
    kind: "delivered",
    Icon: CheckCircle2,
    tone: "text-primary",
  },
  {
    key: "email.opened",
    label: "Opened",
    kind: "opened",
    Icon: Eye,
    tone: "text-foreground",
  },
  {
    key: "email.clicked",
    label: "Clicked",
    kind: "clicked",
    Icon: MousePointerClick,
    tone: "text-secondary",
  },
  {
    key: "email.bounced",
    label: "Bounced",
    kind: "bounced",
    Icon: MailWarning,
    tone: "text-destructive",
  },
  {
    key: "email.complained",
    label: "Complaints",
    kind: "complained",
    Icon: AlertCircle,
    tone: "text-destructive",
  },
];

const EVENT_STYLE = {
  "email.sent": { chip: "border-border text-muted-foreground", Icon: Send },
  "email.delivered": {
    chip: "border-primary/40 text-primary",
    Icon: CheckCircle2,
  },
  "email.opened": { chip: "border-border text-foreground", Icon: Eye },
  "email.clicked": {
    chip: "border-secondary/50 text-secondary",
    Icon: MousePointerClick,
  },
  "email.bounced": {
    chip: "border-destructive/50 text-destructive",
    Icon: MailWarning,
  },
  "email.complained": {
    chip: "border-destructive/50 text-destructive",
    Icon: AlertCircle,
  },
  "email.failed": {
    chip: "border-destructive/50 text-destructive",
    Icon: AlertCircle,
  },
  "email.delivery_delayed": {
    chip: "border-border text-muted-foreground",
    Icon: Activity,
  },
};

const TEST_EVENTS = [
  "email.delivered",
  "email.opened",
  "email.clicked",
  "email.bounced",
  "email.complained",
];

function safeKey(s) {
  return String(s || "").replace(/[^a-z0-9]/gi, "-").toLowerCase();
}

export default function EmailEventsDashboard() {
  const location = useLocation();
  const navigate = useNavigate();

  const tokens = useMemo(() => loadShareTokens(), []);
  const shareIds = useMemo(() => Object.keys(tokens), [tokens]);

  const urlShareId = useMemo(
    () => new URLSearchParams(location.search).get("share_id"),
    [location.search]
  );
  const initialId = urlShareId || shareIds[0] || "";
  const [shareId, setShareId] = useState(initialId);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEvent, setTestEvent] = useState("email.delivered");
  const [sendingTest, setSendingTest] = useState(false);

  const token = shareId ? tokens[shareId] : null;

  const load = useCallback(async () => {
    if (!shareId || !token) {
      setStats(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/email-events?share_id=${shareId}`, {
        headers: { "X-Owner-Token": token },
      });
      setStats(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to load events");
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [shareId, token]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep URL in sync with the picker so this page is shareable/bookmarkable.
  useEffect(() => {
    if (shareId && urlShareId !== shareId) {
      navigate(`/email-events?share_id=${shareId}`, { replace: true });
    }
  }, [shareId, urlShareId, navigate]);

  const sendTestEvent = async () => {
    if (!shareId || !token) return;
    setSendingTest(true);
    try {
      await api.post(
        `/webhooks/resend/test?share_id=${shareId}&event_type=${testEvent}`,
        null,
        { headers: { "X-Owner-Token": token } }
      );
      toast.success(`Recorded ${testEvent}`);
      setTestOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to send test event");
    } finally {
      setSendingTest(false);
    }
  };

  if (shareIds.length === 0) {
    return (
      <div
        data-testid={EMAIL_EVENTS.empty}
        className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center"
      >
        <Activity aria-hidden="true" className="w-10 h-10 text-primary mx-auto" />
        <h1 className="mt-6 font-display text-4xl tracking-tighter font-light">
          No <span className="text-primary">shares</span> yet.
        </h1>
        <p className="mt-3 text-muted-foreground max-w-lg mx-auto">
          Draft a comparison and share it to start recording email delivery
          events from Resend. This dashboard closes the loop with
          delivered · opened · clicked · bounced signals.
        </p>
        <Link to="/compare">
          <Button className="mt-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            Draft a comparison
          </Button>
        </Link>
      </div>
    );
  }

  const totals = stats?.totals || {};
  const totalEvents = stats?.total_events || 0;
  const recipients = stats?.by_recipient || [];
  const recent = stats?.recent || [];

  return (
    <div
      data-testid={EMAIL_EVENTS.page}
      className="max-w-6xl mx-auto px-6 lg:px-10 py-14"
    >
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold mb-4">
            Email events
          </p>
          <h1 className="font-display text-4xl lg:text-6xl leading-none tracking-tighter font-light">
            Deliverability <span className="text-primary">at a glance</span>.
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            Every Resend webhook — <code className="font-mono text-xs">email.delivered</code>,{" "}
            <code className="font-mono text-xs">email.clicked</code>,{" "}
            <code className="font-mono text-xs">email.bounced</code> — for a
            share lands here, correlated by tag. Point your Resend project
            webhook at{" "}
            <code className="font-mono text-xs">POST /api/webhooks/resend</code>{" "}
            to go live.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid={EMAIL_EVENTS.testTrigger}
            variant="outline"
            className="rounded-full"
            onClick={() => setTestOpen(true)}
            disabled={!shareId}
          >
            <Zap aria-hidden="true" className="w-4 h-4 mr-2" />
            Send test event
          </Button>
          <Button
            data-testid={EMAIL_EVENTS.refresh}
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={load}
            aria-label="Refresh"
          >
            {loading ? (
              <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw aria-hidden="true" className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {shareIds.length > 1 && (
        <div className="mt-8 max-w-md">
          <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold mb-2">
            Comparison
          </p>
          <Select value={shareId} onValueChange={setShareId}>
            <SelectTrigger data-testid={EMAIL_EVENTS.shareSelect}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {shareIds.map((id) => (
                <SelectItem key={id} value={id}>{id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Stat cards */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STAT_CARDS.map(({ key, label, kind, Icon, tone }) => (
          <div
            key={key}
            data-testid={EMAIL_EVENTS.statCard(kind)}
            className="rounded-2xl border border-border bg-card p-5"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground font-semibold">
                {label}
              </span>
              <Icon aria-hidden="true" className={`w-4 h-4 ${tone}`} />
            </div>
            <p className={`mt-3 font-display text-4xl tracking-tighter ${tone}`}>
              {totals[key] || 0}
            </p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground font-mono">
        {totalEvents} total events recorded for share{" "}
        <span className="text-foreground">{shareId}</span>
      </p>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Timeline */}
        <section className="lg:col-span-3">
          <div className="flex items-baseline gap-3 border-b border-border pb-2">
            <h2 className="font-display text-2xl tracking-tight">Recent activity</h2>
            <span className="font-mono text-xs text-muted-foreground">
              {recent.length}
            </span>
          </div>
          {recent.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center bg-card">
              <MailWarning
                aria-hidden="true"
                className="w-8 h-8 text-muted-foreground mx-auto"
              />
              <p className="mt-3 text-sm text-muted-foreground">
                No email events for this share yet. Trigger one from{" "}
                <em>Send test event</em> above to preview the timeline.
              </p>
            </div>
          ) : (
            <ul
              data-testid={EMAIL_EVENTS.timeline}
              className="mt-5 space-y-3"
            >
              {recent.map((e) => {
                const style = EVENT_STYLE[e.event_type] || EVENT_STYLE["email.sent"];
                const Icon = style.Icon;
                return (
                  <li
                    key={e.id}
                    data-testid={EMAIL_EVENTS.timelineRow(e.id)}
                    className="rounded-xl border border-border bg-card p-4 flex items-start gap-3"
                  >
                    <div className={`shrink-0 mt-0.5 ${style.chip.split(" ")[1]}`}>
                      <Icon aria-hidden="true" className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-[10px] font-mono ${style.chip}`}
                        >
                          {e.event_type}
                        </Badge>
                        <span className="text-sm text-foreground truncate">
                          {e.to_email || "(unknown recipient)"}
                        </span>
                      </div>
                      {e.subject && (
                        <p className="mt-1 text-xs text-muted-foreground truncate">
                          {e.subject}
                        </p>
                      )}
                      {e.click_url && (
                        <a
                          href={e.click_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-block text-xs text-secondary hover:underline break-all"
                        >
                          {e.click_url}
                        </a>
                      )}
                      {e.bounce_type && (
                        <p className="mt-1 text-xs text-destructive font-mono">
                          bounce · {e.bounce_type}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-[11px] font-mono text-muted-foreground">
                      {new Date(e.received_at).toLocaleString()}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Recipients */}
        <section className="lg:col-span-2">
          <div className="flex items-baseline gap-3 border-b border-border pb-2">
            <h2 className="font-display text-2xl tracking-tight">By recipient</h2>
            <span className="font-mono text-xs text-muted-foreground">
              {recipients.length}
            </span>
          </div>
          {recipients.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-border p-8 text-center bg-card">
              <p className="text-sm text-muted-foreground">
                No recipients tracked yet.
              </p>
            </div>
          ) : (
            <ul
              data-testid={EMAIL_EVENTS.recipients}
              className="mt-5 space-y-3"
            >
              {recipients.map((r) => (
                <li
                  key={r.email}
                  data-testid={EMAIL_EVENTS.recipientRow(safeKey(r.email))}
                  className="rounded-xl border border-border bg-card p-4"
                >
                  <p className="text-sm font-mono text-foreground truncate">
                    {r.email}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {["delivered", "opened", "clicked", "bounced", "complained"].map(
                      (k) =>
                        r[k] > 0 && (
                          <Badge
                            key={k}
                            variant="outline"
                            className="text-[10px] font-mono"
                          >
                            {k} · {r[k]}
                          </Badge>
                        )
                    )}
                  </div>
                  {r.last_event_at && (
                    <p className="mt-2 text-[11px] font-mono text-muted-foreground">
                      Last: {r.last_event} ·{" "}
                      {new Date(r.last_event_at).toLocaleString()}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent
          data-testid={EMAIL_EVENTS.testDialog}
          className="max-w-md bg-background"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              Simulate a Resend event
            </DialogTitle>
            <DialogDescription>
              Outbound email is mocked until you set{" "}
              <code className="font-mono text-xs">RESEND_API_KEY</code>. Use
              this to preview the dashboard end-to-end.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
              Event type
            </p>
            <Select value={testEvent} onValueChange={setTestEvent}>
              <SelectTrigger data-testid={EMAIL_EVENTS.testEventType}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEST_EVENTS.map((e) => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              data-testid={EMAIL_EVENTS.testSend}
              onClick={sendTestEvent}
              disabled={sendingTest}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {sendingTest ? (
                <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap aria-hidden="true" className="w-4 h-4 mr-2" />
              )}
              Record event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
